import axios from 'axios';
import { StateUSA } from '../../features/Registration/useFormStore';
import { BrokerData } from '../../features/Broker/BrokerSignup';
import { hubspotSMBURL, hubspotEnterpriseURL, hubspotBrokerURL } from '../../constants/Constants';

interface UserData {
  firstName?: string;
  lastName?: string;
  email: string;
  phoneNumber?: string;
  propertyLLC?: string;
}

interface DataType {
  submittedAt: number;
  fields: { name: string; value: string }[];
  context: {
    pageUri: string;
    pageName: string;
  };
}

export const utmCodes = ['utm_term', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_source'];
const hubspotContactMappings = [
  { user: 'firstName', hubspot: 'firstname' },
  { user: 'lastName', hubspot: 'lastname' },
  { user: 'email', hubspot: 'email' },
  { user: 'phoneNumber', hubspot: 'phone' },
  { user: 'propertyLLC', hubspot: 'company' },
] as const;

export const sendSelfServiceUserToHubspot = (
  user: UserData,
  unitsNumber?: number | string,
  sizeOfPortfolio?: string,
  state?: StateUSA,
) => {
  const data = prepareDataForHubspot(
    user,
    'Landlord',
    'Self-Service',
    state,
    unitsNumber,
    sizeOfPortfolio,
  );
  return axios.post(hubspotSMBURL, data);
};

export const sendInstitutionalUserToHubspot = (
  user: UserData,
  sizeOfPortfolio?: string,
  state?: StateUSA,
) => {
  const data = prepareDataForHubspot(
    user,
    'Landlord',
    'Landlord',
    state,
    undefined,
    sizeOfPortfolio,
  );

  return axios.post(hubspotEnterpriseURL, data);
};

export const sendBrokerToHubspot = (broker: BrokerData) => {
  const data = prepareDataForHubspot(
    broker,
    'Broker',
    'Broker',
    broker.state,
    undefined,
    undefined,
  );

  return axios.post(hubspotBrokerURL, data);
};

const prepareDataForHubspot = (
  user: UserData,
  role: string,
  contactType: string,
  state: StateUSA | null | undefined,
  unitsNumber?: number | string,
  sizeOfPortfolio?: string,
) => {
  const data: DataType = {
    submittedAt: Date.now(),
    fields: [
      {
        name: 'inquire_role_residential',
        value: role,
      },
      {
        name: 'contact-type',
        value: contactType,
      },
    ],
    context: {
      pageUri: window.location.href,
      pageName: document.title,
    },
  };

  hubspotContactMappings.forEach((mapping) => {
    data.fields.push({
      name: mapping.hubspot,
      value: user[mapping.user] ? `${user[mapping.user]}` : '',
    });
  });

  if (sizeOfPortfolio) {
    data.fields.push({
      name: 'units_under_management',
      value: `${sizeOfPortfolio.replace(/-/g, '–')}`, // Hubspot wants a slightly larger dash.
    });
  }

  if (unitsNumber) {
    data.fields.push({
      name: 'units_managed',
      value: `${unitsNumber}`,
    });
  }

  if (state) {
    data.fields.push({
      name: 'state',
      value: state.label,
    });
  }

  utmCodes.forEach((code) => {
    const utm = sessionStorage.getItem(`${code}*`);
    if (utm) {
      data.fields.push({
        name: code,
        value: utm,
      });
    }
  });

  return data;
};
